import * as Sentry from '@sentry/react';
import type { Event } from '@sentry/react';
import { readEnv } from './env';
import { filterSurveyMetric, filterSurveySpan, filterSurveyTransaction } from '../flow/presentation/surveyObservability';
import { makeSurveyTelemetryTransport } from './surveyTelemetryTransport';

const SENTRY_DSN = readEnv('VITE_SENTRY_DSN');
const SENTRY_ENVIRONMENT = readEnv('VITE_SENTRY_ENVIRONMENT', import.meta.env.MODE || 'development');
const APP_VERSION = readEnv('VITE_APP_VERSION', 'local-dev');

const PICTURE_IN_PICTURE_ACTIVATION_ERROR = 'The request is not triggered by a user activation.';
const EXTENSION_TAB_NOT_FOUND_ERROR = 'Invalid call to runtime.sendMessage(). Tab not found.';

export function isNativePictureInPictureActivationError(event: Event): boolean {
  return Boolean(event.exception?.values?.some((exception) =>
    exception.type === 'NotAllowedError'
    && exception.value === PICTURE_IN_PICTURE_ACTIVATION_ERROR
    && exception.stacktrace?.frames?.some((frame) => frame.function === 'requestPictureInPicture')
  ));
}

export function isExtensionTabNotFoundError(event: Event): boolean {
  const exceptions = event.exception?.values;
  const exception = exceptions?.[0];

  return exceptions?.length === 1
    && exception?.type === 'Error'
    && exception.value === EXTENSION_TAB_NOT_FOUND_ERROR
    && !exception.stacktrace?.frames?.length;
}

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    sendDefaultPii: true,
    enableMetrics: true,
    beforeSendMetric: filterSurveyMetric,
    beforeSendSpan: filterSurveySpan,
    beforeSendTransaction: filterSurveyTransaction,
    transport: makeSurveyTelemetryTransport,
    release: `cx-client@${APP_VERSION}`,
    environment: SENTRY_ENVIRONMENT,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    beforeSend(event) {
      return isNativePictureInPictureActivationError(event) || isExtensionTabNotFoundError(event)
        ? null
        : event;
    },
    tracesSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.2 : 1.0,
  });
}
